// src/components/manager/shared/DocumentFormFields.jsx
import React, { useState, useEffect } from 'react';
import { 
  FileText, Plus, Edit, Trash2, Search, Filter, Download, Eye,
  Upload, Folder, File, Clock, User, Star, Lock, Share, X,
  Calendar, Tag, Archive, AlertCircle, CheckCircle
} from 'lucide-react';
import { FileUpload } from '../../common/FileUpload';
import { firestoreService } from '../../../services/firestoreService';
import { organisationService } from '../../../services/organisationService';

const DOCUMENT_CATEGORIES = {
  policy: { label: 'Policies', color: 'blue', icon: FileText },
  procedure: { label: 'Procedures', color: 'green', icon: FileText },
  form: { label: 'Forms', color: 'yellow', icon: File },
  template: { label: 'Templates', color: 'purple', icon: File },
  report: { label: 'Reports', color: 'indigo', icon: FileText },
  contract: { label: 'Contracts', color: 'red', icon: FileText },
  manual: { label: 'Manuals', color: 'orange', icon: FileText },
  other: { label: 'Other', color: 'gray', icon: File }
};

const DOCUMENT_STATUS = {
  draft: { label: 'Draft', color: 'gray', icon: Edit },
  review: { label: 'Under Review', color: 'yellow', icon: Clock },
  approved: { label: 'Approved', color: 'green', icon: CheckCircle },
  archived: { label: 'Archived', color: 'red', icon: Archive }
};

const ACCESS_LEVELS = {
  public: { label: 'Public', color: 'green', icon: Share },
  internal: { label: 'Internal', color: 'blue', icon: User },
  restricted: { label: 'Restricted', color: 'orange', icon: Lock },
  confidential: { label: 'Confidential', color: 'red', icon: Lock }
};

export const DocumentFormFields = ({ bookId, templateType, onUpdate, accessLevel = 'internal' }) => {
  const [documents, setDocuments] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedFolder, setSelectedFolder] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'policy',
    status: 'draft',
    accessLevel: 'internal',
    folder: '',
    version: '1.0',
    author: '',
    reviewer: '',
    tags: [],
    expiryDate: '',
    fileUrl: '',
    fileName: '',
    fileSize: 0,
    fileType: '',
    notes: ''
  });

  useEffect(() => {
    loadDocumentData();
  }, [bookId]);

  const loadDocumentData = async () => {
    try {
      setLoading(true);
      const data = await organisationService.getManagementFeatureData(bookId, 'documents');
      setDocuments(data?.documents || []);
      setFolders(data?.folders || []);
    } catch (error) {
      console.error('Error loading document data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveDocumentData = async (updatedDocuments, updatedFolders = folders) => {
    try {
      const stats = {
        totalDocuments: updatedDocuments.length,
        draftDocuments: updatedDocuments.filter(d => d.status === 'draft').length,
        approvedDocuments: updatedDocuments.filter(d => d.status === 'approved').length,
        expiringDocuments: updatedDocuments.filter(d => 
          d.expiryDate && new Date(d.expiryDate) <= new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
        ).length
      };

      await organisationService.updateManagementFeatureData(bookId, 'documents', {
        documents: updatedDocuments,
        folders: updatedFolders,
        lastUpdated: new Date(),
        ...stats
      });
      onUpdate?.();
    } catch (error) {
      console.error('Error saving document data:', error);
      throw error;
    }
  };

  const handleFileUpload = async (file, error) => {
    if (error) {
      alert('Error selecting file: ' + error);
      return;
    }

    setUploading(true);
    try {
      const result = await firestoreService.uploadFile(file, `pb-${bookId}/documents`);
      
      setFormData(prev => ({
        ...prev,
        fileUrl: result.url,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        title: prev.title || file.name.split('.')[0] // Set title from filename if empty
      }));
    } catch (error) {
      alert('Error uploading file: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const documentData = {
        ...formData,
        id: editingDocument?.id || Date.now().toString(),
        createdAt: editingDocument?.createdAt || new Date(),
        updatedAt: new Date(),
        tags: Array.isArray(formData.tags) ? formData.tags : 
              typeof formData.tags === 'string' ? formData.tags.split(',').map(t => t.trim()) : []
      };

      let updatedDocuments;
      if (editingDocument) {
        updatedDocuments = documents.map(doc => 
          doc.id === editingDocument.id ? documentData : doc
        );
      } else {
        updatedDocuments = [...documents, documentData];
      }

      await saveDocumentData(updatedDocuments);
      setDocuments(updatedDocuments);
      handleCloseForm();
    } catch (error) {
      alert('Error saving document: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      const updatedDocuments = documents.filter(doc => doc.id !== documentId);
      await saveDocumentData(updatedDocuments);
      setDocuments(updatedDocuments);
    } catch (error) {
      alert('Error deleting document: ' + error.message);
    }
  };

  const handleEdit = (document) => {
    setEditingDocument(document);
    setFormData({
      ...document,
      tags: Array.isArray(document.tags) ? document.tags.join(', ') : document.tags || ''
    });
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingDocument(null);
    setFormData({
      title: '', description: '', category: 'policy', status: 'draft',
      accessLevel: 'internal', folder: '', version: '1.0', author: '',
      reviewer: '', tags: [], expiryDate: '', fileUrl: '', fileName: '',
      fileSize: 0, fileType: '', notes: ''
    });
  };

  const createFolder = () => {
    const folderName = prompt('Enter folder name:');
    if (folderName && folderName.trim()) {
      const newFolder = {
        id: Date.now().toString(),
        name: folderName.trim(),
        createdAt: new Date(),
        documentCount: 0
      };
      const updatedFolders = [...folders, newFolder];
      setFolders(updatedFolders);
      saveDocumentData(documents, updatedFolders);
    }
  };

  const isExpiringSoon = (document) => {
    if (!document.expiryDate) return false;
    const expiryDate = new Date(document.expiryDate);
    const today = new Date();
    const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 30 && daysUntilExpiry >= 0;
  };

  const isExpired = (document) => {
    if (!document.expiryDate) return false;
    return new Date(document.expiryDate) < new Date();
  };

  const filteredDocuments = documents.filter(document => {
    const matchesSearch = document.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         document.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         document.author.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || document.category === filterCategory;
    const matchesStatus = filterStatus === 'all' || document.status === filterStatus;
    const matchesFolder = selectedFolder === 'all' || document.folder === selectedFolder;
    
    return matchesSearch && matchesCategory && matchesStatus && matchesFolder;
  });

  if (loading) {
    return <div className="flex justify-center p-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <FileText className="h-7 w-7 text-blue-600" />
            Document Management
          </h3>
          <p className="text-gray-600 mt-1">Organize files, policies, and document workflows</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={createFolder}
            className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 flex items-center gap-2"
          >
            <Folder className="h-4 w-4" />
            New Folder
          </button>
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Document
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Documents</p>
              <p className="text-2xl font-bold text-gray-900">{documents.length}</p>
            </div>
            <FileText className="h-8 w-8 text-blue-600" />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Approved</p>
              <p className="text-2xl font-bold text-green-600">
                {documents.filter(d => d.status === 'approved').length}
              </p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Under Review</p>
              <p className="text-2xl font-bold text-yellow-600">
                {documents.filter(d => d.status === 'review').length}
              </p>
            </div>
            <Clock className="h-8 w-8 text-yellow-600" />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Expiring Soon</p>
              <p className="text-2xl font-bold text-red-600">
                {documents.filter(d => isExpiringSoon(d) || isExpired(d)).length}
              </p>
            </div>
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg border space-y-4 md:space-y-0 md:flex md:items-center md:gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        
        <select
          value={selectedFolder}
          onChange={(e) => setSelectedFolder(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Folders</option>
          {folders.map(folder => (
            <option key={folder.id} value={folder.name}>{folder.name}</option>
          ))}
        </select>
        
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Categories</option>
          {Object.entries(DOCUMENT_CATEGORIES).map(([key, cat]) => (
            <option key={key} value={key}>{cat.label}</option>
          ))}
        </select>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Status</option>
          {Object.entries(DOCUMENT_STATUS).map(([key, status]) => (
            <option key={key} value={key}>{status.label}</option>
          ))}
        </select>
      </div>

      {/* Documents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredDocuments.map(document => {
          const categoryConfig = DOCUMENT_CATEGORIES[document.category] || DOCUMENT_CATEGORIES.other;
          const statusConfig = DOCUMENT_STATUS[document.status] || DOCUMENT_STATUS.draft;
          const accessConfig = ACCESS_LEVELS[document.accessLevel] || ACCESS_LEVELS.internal;
          const CategoryIcon = categoryConfig.icon;
          const StatusIcon = statusConfig.icon;
          const AccessIcon = accessConfig.icon;
          
          const expiringSoon = isExpiringSoon(document);
          const expired = isExpired(document);
          
          return (
            <div key={document.id} className="bg-white rounded-lg border p-6 hover:shadow-md transition-shadow">
              {/* Document Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg bg-${categoryConfig.color}-100`}>
                    <CategoryIcon className={`h-5 w-5 text-${categoryConfig.color}-600`} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{document.title}</h4>
                    <p className="text-sm text-gray-600">{categoryConfig.label}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <div className={`p-1 rounded bg-${accessConfig.color}-100`} title={accessConfig.label}>
                    <AccessIcon className={`h-3 w-3 text-${accessConfig.color}-600`} />
                  </div>
                  <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-${statusConfig.color}-100 text-${statusConfig.color}-700`}>
                    <StatusIcon className="h-3 w-3" />
                    {statusConfig.label}
                  </div>
                </div>
              </div>

              {/* Document Details */}
              <div className="space-y-2 mb-4">
                {document.description && (
                  <p className="text-sm text-gray-600 line-clamp-2">{document.description}</p>
                )}
                
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <span>v{document.version}</span>
                  <span>{new Date(document.updatedAt).toLocaleDateString()}</span>
                </div>

                {document.author && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <User className="h-4 w-4" />
                    {document.author}
                  </div>
                )}

                {document.fileSize && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <File className="h-4 w-4" />
                    {(document.fileSize / 1024 / 1024).toFixed(1)} MB
                  </div>
                )}
              </div>

              {/* Expiry Warning */}
              {(expiringSoon || expired) && (
                <div className={`mb-4 p-2 rounded-lg ${
                  expired 
                    ? 'bg-red-50 border border-red-200' 
                    : 'bg-yellow-50 border border-yellow-200'
                }`}>
                  <div className="flex items-center gap-2 text-sm">
                    <AlertCircle className={`h-4 w-4 ${expired ? 'text-red-600' : 'text-yellow-600'}`} />
                    <span className={expired ? 'text-red-600 font-medium' : 'text-yellow-600'}>
                      {expired 
                        ? `Expired on ${new Date(document.expiryDate).toLocaleDateString()}`
                        : `Expires ${new Date(document.expiryDate).toLocaleDateString()}`
                      }
                    </span>
                  </div>
                </div>
              )}

              {/* Tags */}
              {document.tags && document.tags.length > 0 && (
                <div className="mb-4">
                  <div className="flex flex-wrap gap-1">
                    {(Array.isArray(document.tags) ? document.tags : document.tags.split(','))
                      .slice(0, 3)
                      .map((tag, idx) => (
                        <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                          {tag.trim()}
                        </span>
                      ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="flex items-center gap-2">
                  {document.fileUrl && (
                    <a
                      href={document.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800"
                      title="Download document"
                    >
                      <Download className="h-4 w-4" />
                    </a>
                  )}
                </div>
                
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleEdit(document)}
                    className="p-1 text-gray-400 hover:text-blue-600 rounded"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(document.id)}
                    className="p-1 text-gray-400 hover:text-red-600 rounded"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredDocuments.length === 0 && (
        <div className="text-center py-8">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No documents found</h3>
          <p className="text-gray-600 mb-4">Get started by uploading your first document.</p>
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Add Document
          </button>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <form onSubmit={handleSubmit}>
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b">
                <h3 className="text-xl font-semibold">
                  {editingDocument ? 'Edit Document' : 'Add Document'}
                </h3>
                <button
                  type="button"
                  onClick={handleCloseForm}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Form Content */}
              <div className="p-6 space-y-6">
                {/* File Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Document File</label>
                  <FileUpload
                    onFileSelect={handleFileUpload}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                    maxSize={10}
                    loading={uploading}
                    className="mb-4"
                  >
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors">
                      <Upload className="h-8 w-8 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600 mb-2">Upload Document</p>
                      <p className="text-sm text-gray-500">PDF, Word, Excel, PowerPoint files up to 10MB</p>
                    </div>
                  </FileUpload>
                  
                  {formData.fileName && (
                    <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm text-green-800">Uploaded: {formData.fileName}</p>
                    </div>
                  )}
                </div>

                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Document Title *</label>
                    <input
                      type="text"
                      required
                      value={formData.title}
                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Version</label>
                    <input
                      type="text"
                      value={formData.version}
                      onChange={(e) => setFormData(prev => ({ ...prev, version: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      {Object.entries(DOCUMENT_CATEGORIES).map(([key, cat]) => (
                        <option key={key} value={key}>{cat.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      {Object.entries(DOCUMENT_STATUS).map(([key, status]) => (
                        <option key={key} value={key}>{status.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Access Level</label>
                    <select
                      value={formData.accessLevel}
                      onChange={(e) => setFormData(prev => ({ ...prev, accessLevel: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      {Object.entries(ACCESS_LEVELS).map(([key, access]) => (
                        <option key={key} value={key}>{access.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Folder</label>
                    <select
                      value={formData.folder}
                      onChange={(e) => setFormData(prev => ({ ...prev, folder: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">No Folder</option>
                      {folders.map(folder => (
                        <option key={folder.id} value={folder.name}>{folder.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                  />
                </div>

                {/* People */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Author</label>
                    <input
                      type="text"
                      value={formData.author}
                      onChange={(e) => setFormData(prev => ({ ...prev, author: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Reviewer</label>
                    <input
                      type="text"
                      value={formData.reviewer}
                      onChange={(e) => setFormData(prev => ({ ...prev, reviewer: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Expiry Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Expiry Date</label>
                  <input
                    type="date"
                    value={formData.expiryDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, expiryDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
                  <input
                    type="text"
                    value={formData.tags}
                    onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Comma-separated tags (e.g. policy, HR, confidential)"
                  />
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                    placeholder="Additional notes or comments..."
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
                <button
                  type="button"
                  onClick={handleCloseForm}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving || uploading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {(saving || uploading) && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                  {editingDocument ? 'Update Document' : 'Add Document'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};